import React, { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { z } from 'zod';
import { toast } from 'react-toastify';
import { LogoDropzone } from '../../components/Dropzone';
import { LogoPreview } from '../../components/LogoPreview';
import TextField from '../../components/TextField';
import { axiosPost } from '../../utils/api';

const UploadLogoMethod = {
  UPLOAD: 'UPLOAD',
  URL: 'URL',
};

const schema = z.object({
  companyName: z.string().min(1, 'Company name is required'),
  companyLogo: z.string().optional(),
  website: z.string(),
});

const CompanyDetailsForm = ({ companyData = undefined, isLoading, onConfirm }) => {
  const [companyLogoMethod, setCompanyLogoMethod] = React.useState(UploadLogoMethod.UPLOAD);
  const [uploadedFile, setUploadedFile] = React.useState();
  const [isUploadingLogo, setIsUploadingLogo] = React.useState(false);

  const methods = useForm({
    resolver: zodResolver(schema),
    defaultValues: companyData,
  });

  const { watch, handleSubmit } = methods;

  const getFileURL = (file) => {
    if (!file) {
      return null;
    }
    return URL.createObjectURL(file);
  };

  const companyLogo = watch('companyLogo');
  const previewValue = companyLogoMethod === UploadLogoMethod.UPLOAD ? getFileURL(uploadedFile) : companyLogo;
  const hasPreview = !!previewValue;

  const onDrop = useCallback(
    async (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (!file) return;

      setUploadedFile(file);
    },
    [setUploadedFile],
  );

  const onSubmit = async (data) => {
    if (companyLogoMethod === UploadLogoMethod.UPLOAD) {
      if (!uploadedFile) {
        onConfirm(data);
        return;
      }
      try {
        setIsUploadingLogo(true);
        const formData = new FormData();
        formData.append('logo', uploadedFile);
        const res = await axiosPost('/my-company/logo', formData);
        data.companyLogo = res.data.url;
      } catch (err) {
        console.error(err);
        toast.error(err?.response?.data?.message || 'Failed to upload logo');
        setIsUploadingLogo(false);
        return;
      } finally {
        setIsUploadingLogo(false);
      }
    }
    onConfirm(data);
  };

  return (
    <Box
      display='flex'
      flexDirection='column'
      alignItems='center'
      justifyContent='center'
      minHeight='100%'
      paddingX={2}
      paddingY={4}
    >
      <Typography variant='h4' gutterBottom>
        {!companyData ? 'Create your company' : 'Edit your company'}
      </Typography>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <TextField label='Company Name' name='companyName' />
          <TextField label='Website URL' name='website' />

          <RadioGroup style={{ justifyContent: 'center' }}>
            <Box display='flex' alignItems='center' gap={1}>
              <FormControlLabel
                value={UploadLogoMethod.UPLOAD}
                control={
                  <Radio
                    checked={companyLogoMethod === UploadLogoMethod.UPLOAD}
                    onChange={() => setCompanyLogoMethod(UploadLogoMethod.UPLOAD)}
                  />
                }
                label='Upload Logo Image'
                sx={{
                  mr: 2,
                }}
              />
              <Typography>or</Typography>
              <FormControlLabel
                value={UploadLogoMethod.URL}
                control={
                  <Radio
                    checked={companyLogoMethod === UploadLogoMethod.URL}
                    onChange={() => setCompanyLogoMethod(UploadLogoMethod.URL)}
                  />
                }
                label='Enter Logo URL'
                sx={{
                  ml: 0,
                }}
              />
            </Box>
          </RadioGroup>
          {companyLogoMethod === UploadLogoMethod.UPLOAD ? (
            <LogoDropzone onDrop={onDrop} />
          ) : (
            <TextField label='Company Logo URL' name='companyLogo' margin='none' />
          )}
          {hasPreview && <LogoPreview logoUrl={previewValue} />}

          <Box mt={3} mb={2}>
            <Button
              type='submit'
              variant='contained'
              color='primary'
              fullWidth
              disabled={isLoading || isUploadingLogo}
            >
              {isUploadingLogo ? 'Uploading logo…' : 'Submit'}
            </Button>
          </Box>
        </form>
      </FormProvider>
    </Box>
  );
};

export default CompanyDetailsForm;
